import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Settings, ChevronDown, ChevronRight, AlertCircle, Check, Info, Search, Building2, X, Trash2, Loader2, Lock } from 'lucide-react';
import { adminFeatureFlagsAPI } from '../../services/api';
import api from '../../services/api';
import { useNotInPlanMessage } from '../../components/common/NotInPlan';

// Category display names and icons
const CATEGORY_INFO = {
  modules: { name: 'Modules', description: 'Enable or disable portal sections' },
  payments: { name: 'Payments', description: 'Payment and billing features' },
  tickets: { name: 'Tickets', description: 'Support ticket features' },
  invoices: { name: 'Invoices', description: 'Invoice management features' },
  quotes: { name: 'Quotes', description: 'Quote management features' },
  ui: { name: 'User Interface', description: 'UI customization options' },
  notifications: { name: 'Notifications', description: 'Email notification settings' },
  general: { name: 'General', description: 'General system settings' }
};

export default function AdminFeatureFlagsPage() {
  const queryClient = useQueryClient();
  const [expandedCategories, setExpandedCategories] = useState(new Set(['modules', 'payments', 'tickets']));
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingFlags, setUpdatingFlags] = useState(new Set());
  const [overridesModal, setOverridesModal] = useState({ open: false, flagKey: null, flagName: null });
  const notInPlan = useNotInPlanMessage();
  const [saveError, setSaveError] = useState(null);

  // Fetch all feature flags grouped by category
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-feature-flags'],
    queryFn: async () => {
      const response = await adminFeatureFlagsAPI.list();
      return response.data.flags;
    }
  });

  // Update flag mutation
  const updateFlagMutation = useMutation({
    mutationFn: async ({ key, updates }) => {
      const response = await adminFeatureFlagsAPI.update(key, updates);
      return response.data;
    },
    onMutate: async ({ key }) => {
      setUpdatingFlags(prev => new Set([...prev, key]));
    },
    onSuccess: () => {
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: ['admin-feature-flags'] });
    },
    onError: (error) => {
      // 409 NOT_ENTITLED: the backend refused to switch the flag on.
      setSaveError(error?.response?.data?.error || error.message);
    },
    onSettled: (data, error, { key }) => {
      setUpdatingFlags(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  });

  // Fetch overrides for a specific flag
  const { data: overridesData, isLoading: overridesLoading } = useQuery({
    queryKey: ['flag-overrides', overridesModal.flagKey],
    queryFn: async () => {
      const response = await api.get(`/admin/feature-flags/${overridesModal.flagKey}/overrides`);
      return response.data.companies;
    },
    enabled: overridesModal.open && !!overridesModal.flagKey
  });

  // Remove override mutation
  const removeOverrideMutation = useMutation({
    mutationFn: async ({ companyId, flagKey }) => {
      await api.delete(`/admin/companies/${companyId}/feature-flags/${flagKey}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flag-overrides', overridesModal.flagKey] });
      queryClient.invalidateQueries({ queryKey: ['admin-feature-flags'] });
    }
  });

  // Group flags by category
  const groupedFlags = (data || []).reduce((acc, flag) => {
    const category = flag.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(flag);
    return acc;
  }, {});

  // Filter flags by search query
  const filterFlags = (flags) => {
    if (!searchQuery) return flags;
    const query = searchQuery.toLowerCase();
    return flags.filter(flag =>
      flag.name.toLowerCase().includes(query) ||
      flag.key.toLowerCase().includes(query) ||
      (flag.description && flag.description.toLowerCase().includes(query))
    );
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleToggleFlag = async (flag) => {
    const newValue = !(flag.default_value === true || flag.default_value === 'true');
    // A locked flag may be switched off (it reads as off anyway) but never on.
    if (newValue && flag.entitled === false) return;
    await updateFlagMutation.mutateAsync({
      key: flag.key,
      updates: { defaultValue: newValue }
    }).catch(() => {});
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <p>Failed to load feature flags: {error.message}</p>
      </div>
    );
  }

  const categories = Object.keys(groupedFlags).sort((a, b) => {
    // Put 'modules' first, then alphabetically
    if (a === 'modules') return -1;
    if (b === 'modules') return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Feature Flags
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Enable or disable features across the portal
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search flags..."
          className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-800 dark:text-white"
        />
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p className="font-medium">System-wide defaults</p>
          <p className="mt-1">
            These are the default settings for all companies. You can override specific flags
            for individual companies from the company detail page. A flag marked with a lock
            belongs to a module your plan does not include: it can be switched off, never on.
          </p>
        </div>
      </div>

      {saveError && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3 text-sm text-amber-800 dark:text-amber-300">
          <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{saveError}</span>
          <button type="button" className="ml-auto underline" onClick={() => setSaveError(null)}>Dismiss</button>
        </div>
      )}

      {/* Flag Categories */}
      <div className="space-y-4">
        {categories.map(category => {
          const categoryInfo = CATEGORY_INFO[category] || { name: category, description: '' };
          const flags = filterFlags(groupedFlags[category] || []);
          const isExpanded = expandedCategories.has(category);

          if (flags.length === 0 && searchQuery) return null;

          return (
            <div key={category} className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full px-4 py-3 flex items-center justify-between bg-neutral-50 dark:bg-neutral-700/50 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                  )}
                  <div className="text-left">
                    <h3 className="font-medium text-neutral-900 dark:text-white">{categoryInfo.name}</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">{categoryInfo.description}</p>
                  </div>
                </div>
                <span className="text-sm text-neutral-500 dark:text-neutral-300 bg-neutral-200 dark:bg-neutral-600 px-2 py-1 rounded-full">
                  {flags.length} flags
                </span>
              </button>

              {/* Flags List */}
              {isExpanded && (
                <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
                  {flags.map(flag => {
                    const locked = flag.entitled === false;
                    // A locked flag reads as off, whatever the row says.
                    const isEnabled = !locked && (flag.default_value === true || flag.default_value === 'true');
                    const isUpdating = updatingFlags.has(flag.key);
                    const disabled = isUpdating || !flag.is_active || locked;

                    return (
                      <div
                        key={flag.key}
                        className="px-4 py-4 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-700/50"
                      >
                        <div className="flex-1 min-w-0 pr-4">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-neutral-900 dark:text-white">{flag.name}</h4>
                            {!flag.is_active && (
                              <span className="text-xs bg-neutral-200 dark:bg-neutral-600 text-neutral-600 dark:text-neutral-300 px-2 py-0.5 rounded">
                                Inactive
                              </span>
                            )}
                            {locked && (
                              <span
                                className="inline-flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded"
                                title={notInPlan}
                              >
                                <Lock className="w-3 h-3" /> {notInPlan}
                              </span>
                            )}
                            {flag.overrideCount > 0 && (
                              <button
                                onClick={() => setOverridesModal({ open: true, flagKey: flag.key, flagName: flag.name })}
                                className="inline-flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded hover:bg-amber-200 dark:hover:bg-amber-900 transition-colors"
                              >
                                <Building2 className="w-3 h-3" />
                                {flag.overrideCount} {flag.overrideCount === 1 ? 'override' : 'overrides'}
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{flag.description}</p>
                          <code className="text-xs text-neutral-400 dark:text-neutral-500 mt-1 block">{flag.key}</code>
                        </div>
                        <button
                          onClick={() => handleToggleFlag(flag)}
                          disabled={disabled}
                          title={locked ? notInPlan : undefined}
                          className={`flex-shrink-0 relative w-14 h-8 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                            isEnabled
                              ? 'bg-primary-500'
                              : 'bg-neutral-300'
                          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span
                            className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transform transition-transform ${
                              isEnabled ? 'translate-x-6' : 'translate-x-0'
                            } flex items-center justify-center`}
                          >
                            {isUpdating ? (
                              <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                            ) : isEnabled ? (
                              <Check className="w-3 h-3 text-primary-500" />
                            ) : null}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {categories.length === 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-12 text-center">
          <Settings className="w-12 h-12 mx-auto mb-4 text-neutral-400 dark:text-neutral-500" />
          <p className="text-neutral-500 dark:text-neutral-400">No feature flags configured</p>
        </div>
      )}

      {/* Overrides Modal */}
      {overridesModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Company Overrides</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{overridesModal.flagName}</p>
              </div>
              <button
                onClick={() => setOverridesModal({ open: false, flagKey: null, flagName: null })}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {overridesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                </div>
              ) : overridesData?.length === 0 ? (
                <p className="text-center text-neutral-500 dark:text-neutral-400 py-8">
                  No company overrides for this flag
                </p>
              ) : (
                <div className="space-y-3">
                  {overridesData?.map(company => (
                    <div
                      key={company.companyId}
                      className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Building2 className="w-5 h-5 text-neutral-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <Link
                            to={`/admin/companies/${company.companyId}`}
                            className="font-medium text-neutral-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 truncate block"
                          >
                            {company.companyName}
                          </Link>
                          <span className="text-xs text-neutral-500 dark:text-neutral-400">
                            ID: {company.companyId}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`text-sm font-medium px-2 py-1 rounded ${
                          company.value === true || company.value === 'true'
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                        }`}>
                          {company.value === true || company.value === 'true' ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={() => removeOverrideMutation.mutate({
                            companyId: company.companyId,
                            flagKey: overridesModal.flagKey
                          })}
                          disabled={removeOverrideMutation.isPending}
                          className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                          title="Remove override"
                        >
                          {removeOverrideMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-700/50">
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Overrides take precedence over the system default. Removing an override will revert the company to the default value.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
